// src/pages/SuscripcionPage.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../axiosInstance';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';


const SuscripcionPage = () => {
  const { suscripcion, loading: authLoading, refreshAuthData, user } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('status');

  const [renovacionAutomatica, setRenovacionAutomatica] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    if (suscripcion) {
      setRenovacionAutomatica(Boolean(suscripcion.renovacion_automatica));
    }
  }, [suscripcion]);

  useEffect(() => {
    if (paymentStatus === 'pending' && typeof refreshAuthData === 'function') {
      const iv = setInterval(() => { refreshAuthData(); }, 5000);
      const stop = setTimeout(() => clearInterval(iv), 60000);
      return () => { clearInterval(iv); clearTimeout(stop); };
    }
  }, [paymentStatus, refreshAuthData]);

  const handleRenovacionAutomaticaToggle = async (event) => {
    const nuevoValor = event.target.checked;

    // Si está activando de nuevo, solo actualizar en BD (no crear nueva sub en N1CO por ahora)
    if (nuevoValor) {
      try {
        setSaving(true);
        setRenovacionAutomatica(true);
        await axiosInstance.patch('/suscripcion/renovacion-automatica/', { renovacion_automatica: true });
        setNotification({ open: true, message: 'Renovación automática reactivada', severity: 'success' });
        if (typeof refreshAuthData === 'function') await refreshAuthData();
      } catch (error) {
        setRenovacionAutomatica(false);
        setNotification({ open: true, message: 'Error al reactivar', severity: 'error' });
      } finally {
        setSaving(false);
      }
      return;
    }

    // Si está desactivando, abrir diálogo de confirmación
    setCancelDialogOpen(true);
  };

  const handleCancelarSuscripcion = async () => {
    setCancelando(true);
    try {
      const response = await axiosInstance.post('/n1co/cancelar-suscripcion/');
      if (response.data.success) {
        setCancelDialogOpen(false);
        setRenovacionAutomatica(false);
        setNotification({ open: true, message: response.data.message, severity: 'warning' });
        if (typeof refreshAuthData === 'function') await refreshAuthData();
      } else {
        setNotification({ open: true, message: response.data.error || 'Error al cancelar', severity: 'error' });
      }
    } catch (error) {
      setNotification({ open: true, message: error.response?.data?.error || 'Error al cancelar la suscripción', severity: 'error' });
    } finally {
      setCancelando(false);
    }
  };

  const handleCloseNotification = () => setNotification((prev) => ({ ...prev, open: false }));

  if (authLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  const usuarioTienePlanActivo = Boolean(suscripcion?.plan && suscripcion?.estado === 'Activa');

  const fechaFinLegible = suscripcion?.fecha_fin
    ? new Date(suscripcion.fecha_fin).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>Gestionar Suscripción</Typography>

      {paymentStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>¡Pago exitoso! Tu suscripción ha sido activada.</Alert>
      )}
      {paymentStatus === 'failed' && (
        <Alert severity="error" sx={{ mb: 3 }}>El pago falló o fue cancelado. Por favor, intenta de nuevo.</Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          {usuarioTienePlanActivo ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="h6" component="div">Suscripción Activa</Typography>
              </Stack>
              <Divider />
              <Box>
                <Typography variant="subtitle1" component="div" fontWeight="bold">
                  {suscripcion.plan.nombre}
                </Typography>
                <Chip label={suscripcion.estado} color="success" size="small" sx={{ mt: 0.5 }} />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">Vence el: {fechaFinLegible}</Typography>
              </Stack>

              {/* Toggle de renovación automática */}
              <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #eee' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <AutorenewIcon color={renovacionAutomatica ? 'primary' : 'disabled'} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Renovación automática
                  </Typography>
                </Stack>

                <FormControlLabel
                  control={
                    <Switch
                      checked={renovacionAutomatica}
                      onChange={handleRenovacionAutomaticaToggle}
                      color="primary"
                      disabled={saving}
                    />
                  }
                  label={renovacionAutomatica ? 'Activada' : 'Desactivada'}
                />

                {/* Texto explicativo según estado */}
                {renovacionAutomatica ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mt: 1.5, borderRadius: 2, borderColor: 'primary.light', bgcolor: 'primary.50' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.2 }} />
                      <Box>
                        <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ mb: 0.5 }}>
                          Tu suscripción se renovará automáticamente
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          El <strong>{fechaFinLegible}</strong> se realizará un cobro automático para continuar con tu plan.
                          Si deseas cancelar, desactiva este toggle antes de esa fecha.
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mt: 1.5, borderRadius: 2, borderColor: 'warning.light', bgcolor: '#fffbf0' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20, mt: 0.2 }} />
                      <Box>
                        <Typography variant="body2" color="warning.dark" fontWeight={600} sx={{ mb: 0.5 }}>
                          Tu suscripción no se renovará
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Seguirás teniendo acceso completo a la plataforma hasta el <strong>{fechaFinLegible}</strong>.
                          Después de esa fecha:
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            ✅ Podrás seguir iniciando sesión
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ✅ Podrás consultar tus pacientes existentes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ✅ Podrás ver el historial de consultas
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ❌ No podrás agregar nuevos pacientes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ❌ No tendrás acceso a funciones premium
                          </Typography>
                        </Stack>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          💡 Si cambias de opinión, activa el toggle nuevamente antes del <strong>{fechaFinLegible}</strong> para reactivar la renovación automática.
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center" sx={{ p: 2 }}>
              <WarningAmberIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography variant="h6" component="div">Suscripción Requerida</Typography>
              <Typography variant="body2" color="text.secondary">
                No tienes un plan activo. ¡Elige uno para empezar!
              </Typography>
              <Button variant="contained" color="primary" onClick={() => navigate('/doctor/planes')} sx={{ mt: 1 }}>
                Ver Planes
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Notificación */}
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Diálogo de confirmación de cancelación */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'warning.dark' }}>
          ⚠️ ¿Cancelar renovación automática?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Al desactivar la renovación automática:
          </Typography>
          <Stack spacing={0.8} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              ✅ Seguirás con acceso completo hasta el <strong>{fechaFinLegible}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ✅ No se realizarán más cobros automáticos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ✅ Podrás reactivar antes de que venza si cambias de opinión
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ❌ Al vencer, perderás acceso a funciones premium
            </Typography>
          </Stack>
          <Alert severity="info" sx={{ mt: 1 }}>
            Puedes reactivar la renovación automática en cualquier momento antes del <strong>{fechaFinLegible}</strong>.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCancelDialogOpen(false)} variant="outlined" disabled={cancelando}>
            Mantener renovación
          </Button>
          <Button
            onClick={handleCancelarSuscripcion}
            variant="contained"
            color="warning"
            disabled={cancelando}
            startIcon={cancelando ? <CircularProgress size={16} /> : null}
          >
            {cancelando ? 'Cancelando...' : 'Sí, cancelar renovación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuscripcionPage;
